import {
  getCompanyId,
  getSelectedRegion,
  getSelectedUF,
} from "../services/company.api";
import { triggerVersionUpdate } from "../utils/version-event";
import { redirectToLogin } from "./auth-session.helpers";
const VERSION_KEY = "@Locavibe:app_version";
export type ApiResponseType<T> = {
  page: unknown;
  totalItems: unknown;
  success: import("../../types/devolution-deposit").IDevolutionDeposit[];
  vehiclesByCity: ApiResponseType<unknown>;
  ok: boolean;
  data: T;
  message?: string;
  errors?: Record<string, unknown> | [Record<string, unknown>];
};

interface MakeRequestOptions extends RequestInit {
  skipUFParam?: boolean;
  skipUFHeader?: boolean;
}

export async function makeRequest<T>(
  url: string,
  options: MakeRequestOptions = {}
) {
  try {
    const isApiRequest = url.startsWith(import.meta.env.VITE_API_URL);

    if (isApiRequest && window["z__z"]) {
      options.headers = options.headers || {};
      const token = window["z__z"]();

      const companyId = getCompanyId();
      const currentUF = getSelectedUF();
      const currentRegion = getSelectedRegion();

      Object.assign(options.headers, {
        authorization: `Bearer ${token}`,
        ...(companyId && { companyId: companyId }),
        ...(!options.skipUFHeader && currentUF && { currentUF: currentUF }),
        ...(!options.skipUFHeader &&
          currentRegion && { currentRegion: currentRegion }),
      });
    }

    const urlObj = new URL(url);

    const currentUFForParam = getSelectedUF();
    const currentRegionForParam = getSelectedRegion();
    if (!options.skipUFParam && currentUFForParam) {
      urlObj.searchParams.set("currentUF", currentUFForParam);
    }
    if (!options.skipUFParam && currentRegionForParam) {
      urlObj.searchParams.set("currentRegion", currentRegionForParam);
    }
    const result = await fetch(urlObj.toString(), options);
    const serverVersion = result.headers.get("x-app-version");

    if (serverVersion) {
      const localVersion = localStorage.getItem(VERSION_KEY);

      if (!localVersion) {
        localStorage.setItem(VERSION_KEY, serverVersion);
      } else if (serverVersion !== localVersion) {
        triggerVersionUpdate(serverVersion);
      }
    }

    if (isApiRequest && result.status === 401) {
      redirectToLogin();

      return {
        ok: false,
        data: {} as T,
        message: "Sessao expirada. Faca login novamente.",
      };
    }

    const response = (await result.json()) as ApiResponseType<T>;

    return response;
  } catch (error) {
    console.error("Erro inesperado:", error);
    return {
      ok: false,
      data: {} as T,
      errors: error,
      message: (error as { message: string }).message,
    };
  }
}

export type ApiResponseDownload = {
  ok: boolean;
  data: Blob | null;
  message?: string;
  errors?: Record<string, string | string[]>;
};

export async function downloadRequest(
  url: string,
  options: RequestInit = {}
): Promise<ApiResponseDownload> {
  try {
    const isApiRequest = url.startsWith(import.meta.env.VITE_API_URL);

    if (isApiRequest && window["z__z"]) {
      options.headers = options.headers || {};
      const token = window["z__z"]();

      const companyId = getCompanyId();
      const currentUF = getSelectedUF();
      const currentRegion = getSelectedRegion();

      Object.assign(options.headers, {
        authorization: `Bearer ${token}`,
        ...(companyId && { companyId: companyId }),
        ...(currentUF && { currentUF: currentUF }),
        ...(currentRegion && { currentRegion: currentRegion }),
      });
    }

    const response = await fetch(url, options);

    if (isApiRequest && response.status === 401) {
      redirectToLogin();

      return {
        ok: false,
        data: null,
        message: "Sessao expirada. Faca login novamente.",
      };
    }

    if (!response.ok) {
      const text = await response.text();
      return {
        ok: false,
        data: null,
        errors: { general: text },
        message: `Erro ao baixar arquivo: ${response.status} ${response.statusText}`,
      };
    }

    const contentType = response.headers.get("content-type");

    if (contentType && contentType.includes("application/pdf")) {
      const blob = await response.blob();
      return { ok: true, data: blob };
    } else {
      const text = await response.text();
      return {
        ok: false,
        data: null,
        errors: { general: text },
        message: "Resposta não é um PDF válido",
      };
    }
  } catch (error) {
    console.error({
      ok: false,
      data: null,
      errors: {
        general: error instanceof Error ? error.message : String(error),
      },
      message:
        error instanceof Error
          ? error.message
          : "Erro inesperado ao baixar arquivo",
    });

    return {
      ok: false,
      data: null,
      errors: {
        general: error instanceof Error ? error.message : String(error),
      },
      message:
        error instanceof Error
          ? error.message
          : "Erro inesperado ao baixar arquivo",
    };
  }
}
